import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';

import { createConstantThunk, editConstantThunk, getConstantThunk } from 'redux/thunks/constant';
import { Constant, ParamsConstantForForm, TYPES } from 'types/constant';
import { RootState, useDispatch, useSelector } from '../../redux/store';
import { getConstant, resetError } from '../../redux/slices/constant';
import { EditFooter } from 'components/EditFooter/EditFooter';
import { EditHeader } from 'components/EditHeader/EditHeader';
import { PATH_DASHBOARD } from '../../routes/paths';
import { LoadingScreen } from 'components/LoadingScreen';
import { TextEditor } from 'components/TextEditor';
import { FormProvider } from 'components/hook-form/FormProvider';
import { RHFTextField } from 'components/hook-form/RHFTextField';
import { RHFSelect } from 'components/hook-form/RHFSelect';

interface FormValuesProps extends ParamsConstantForForm {}

const ConstantCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { error, constant } = useSelector((state: RootState) => state.constant);

  const { enqueueSnackbar } = useSnackbar();
  const { pathname } = useLocation();
  const { id = '' } = useParams();
  const [typeRedactor, setTypeRedactor] = useState<Constant['type']>('list');
  const [isLoaded, setLoaded] = useState(true);

  const isEdit = pathname.includes('edit');

  const constantSchema = Yup.object().shape({
    key: Yup.string().required('Название обязательно к заполнению'),
    type: Yup.string().required('Текст обязателен к заполнению'),
    value: Yup.string().required('Ссылка обязательна к заполнению'),
  });

  const defaultValues = useMemo(
    () => ({
      key: constant?.key || '',
      type: constant?.type || 'list',
      value: constant?.value || '',
    }),
    [constant]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(constantSchema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const isHtmlType = typeRedactor === 'html';

  const isListType = typeRedactor === 'list';

  useEffect(() => {
    const load = async () => {
      if (id) {
        await dispatch(getConstantThunk(Number(id)));
      } else {
        dispatch(getConstant(null));
      }

      setLoaded(true);
    };

    load();
  }, [dispatch, id]);

  useEffect(() => {
    reset(defaultValues);
  }, [isEdit, constant, reset, defaultValues]);

  useEffect(() => {
    if (error) {
      const objErrors = error.response?.data?.errors;
      if (objErrors) {
        for (const err in objErrors) {
          enqueueSnackbar(objErrors[err], { variant: 'error' });
        }
        dispatch(resetError());
      } else {
        enqueueSnackbar(isEdit ? 'Ошибка при обновлении' : 'Ошибка при создании', {
          variant: 'error',
        });
        dispatch(resetError());
      }
    }
  }, [dispatch, enqueueSnackbar, error, isEdit]);

  const setContent = (value: string) => {
    setValue('value', value);
  };

  const onSubmit = async (data: FormValuesProps) => {
    const dataParams = {
      ...data,
      type: typeRedactor,
    };
    const result = isEdit
      ? await dispatch(editConstantThunk(Number(constant?.id) || 0, dataParams))
      : await dispatch(createConstantThunk(dataParams));

    if (result) {
      reset();
      enqueueSnackbar(!isEdit ? 'Константа создана' : 'Константа обновлена');
      navigate(PATH_DASHBOARD.constant.root);
    }
  };

  return (
    <>
      {isLoaded ? (
        <>
          <EditHeader
            title={isEdit ? 'Отредактировать константу' : 'Создать константу'}
            link={PATH_DASHBOARD.constant.root}
          />

          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' },
              }}
            >
              <RHFTextField name="key" label="Название" />

              <RHFSelect
                onChange={(val) => setTypeRedactor(val.target.value as Constant['type'])}
                name=""
                label="Тип"
              >
                <option value={typeRedactor}>{''}</option>
                {TYPES.map(({ option, label }) => (
                  <option key={option} value={label}>
                    {label}
                  </option>
                ))}
              </RHFSelect>

              {isListType && <RHFTextField name="value" label="Значение" />}

              {isHtmlType && (
                <TextEditor value={constant ? constant.value : ''} onChange={setContent} />
              )}
            </Box>
            <EditFooter
              backButton={PATH_DASHBOARD.constant.root}
              isEdit={isEdit}
              showLoader={isSubmitting}
            />
          </FormProvider>
        </>
      ) : (
        <LoadingScreen />
      )}
    </>
  );
};

export default ConstantCreate;
